import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { createContext, useContext, useState } from 'react'
import {
  ArrowDownward,
  ArrowUpward,
  Delete,
  Edit,
  ExpandMore,
} from '@mui/icons-material'
import AttributesTable from '../PhaseAttributes/AttributesTable'
import { tsStudyPhase } from '../../../Typescript/StuartPy/StudyPhase'
import DeletePhaseModal from './DeletePhaseModal'
import { useUserContext } from '../../../Contexts/UserConfig'
import { useStudyContext } from '../../../Contexts/Modules/StudyModule/SelectedStudyModule'
import { useMutation, useQueryClient } from 'react-query'
import { AxiosResponse } from 'axios'
import PyAPI from '../../../APIs/Python/APIs/PyAPI'
import { useParams } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'

export const PhaseContext = createContext<tsStudyPhase>({} as tsStudyPhase)
export const usePhaseContext = () => useContext(PhaseContext)

function PhaseAccordion({
  index,
  phase,
}: {
  index: number
  phase: tsStudyPhase
}) {
  const queryClient = useQueryClient()
  const { whoAmI } = useUserContext()
  const { study_id } = useParams()

  const { mutate: updatePhase, isLoading } = useMutation<
    any,
    AxiosResponse,
    { phase_id: string; data: any },
    () => void
  >(
    ({ phase_id, data }) =>
      PyAPI.patch(
        whoAmI,
        `/study/${study_id}/phase?phase_id=${phase_id}`,
        data,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['PyAPI', '/study'],
        })
      },
    },
  )

  const { studyQuery } = useStudyContext()
  const { loginConfig } = useUserContext()

  const [activeEdit, setActiveEdit] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  return (
    <Box
      component={Paper}
      key={index}
      sx={{ my: 1 / 2 }}
    >
      <PhaseContext.Provider value={phase}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              flexDirection: 'row-reverse',
              '& .MuiAccordionSummary-content': {
                m: 0,
              },
            }}
          >
            <Box sx={{ mx: 2, width: 1 }}>
              {activeEdit ?
                <TextField
                  defaultValue={phase?.PhaseName}
                  onBlur={(e) => {
                    if (
                      phase.PhaseName !== e.target.value &&
                      studyQuery.data?.phases
                    ) {
                      updatePhase({
                        phase_id: phase.PhaseID,
                        data: {
                          PhaseName: e.target.value,
                        },
                      })
                    }
                    setActiveEdit(false)
                  }}
                  margin="dense"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': {
                      py: 1 / 2,
                      my: 0,
                    },
                  }}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              : <Typography>{phase.PhaseName}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', ml: 'auto' }}>
              <LoadingButton
                loading={isLoading}
                disabled={
                  !studyQuery.data?.phases ||
                  index === 0 ||
                  !loginConfig?.Modules?.EditStudy ||
                  study_id === '1'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  if (studyQuery.data?.phases) {
                    updatePhase({
                      phase_id: phase?.PhaseID,
                      data: {
                        PhaseSeq: index - 1,
                      },
                    })
                    updatePhase({
                      phase_id: studyQuery.data.phases[index - 1].PhaseID,
                      data: {
                        PhaseSeq: index,
                      },
                    })
                  }
                }}
                sx={{ my: -0.5 }}
              >
                <ArrowUpward />
              </LoadingButton>
              <LoadingButton
                loading={isLoading}
                disabled={
                  !studyQuery.data?.phases ||
                  index === studyQuery.data.phases.length - 1 ||
                  !loginConfig?.Modules?.EditStudy ||
                  study_id === '1'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  if (studyQuery.data?.phases) {
                    updatePhase({
                      phase_id: phase?.PhaseID,
                      data: {
                        PhaseSeq: index + 1,
                      },
                    })
                    updatePhase({
                      phase_id: studyQuery.data.phases[index + 1].PhaseID,
                      data: {
                        PhaseSeq: index,
                      },
                    })
                  }
                }}
                sx={{ my: -0.5 }}
              >
                <ArrowDownward />
              </LoadingButton>
              <LoadingButton
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveEdit(true)
                }}
                sx={{ my: -0.5 }}
                disabled={
                  activeEdit ||
                  !loginConfig?.Modules?.EditStudy ||
                  study_id === '1'
                }
              >
                <Edit />
              </LoadingButton>
              <Tooltip
                title={
                  phase?.attributes.length > 0 ? 'Delete attributes first' : ''
                }
              >
                <LoadingButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteModal(true)
                  }}
                  sx={{ my: -0.5 }}
                  disabled={
                    deleteModal ||
                    !loginConfig?.Modules?.EditStudy ||
                    study_id === '1' ||
                    phase?.attributes.length > 0
                  }
                >
                  <Delete />
                </LoadingButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <AttributesTable phase={phase} />
          </AccordionDetails>
        </Accordion>
        <DeletePhaseModal
          open={deleteModal}
          setOpen={setDeleteModal}
        />
      </PhaseContext.Provider>
    </Box>
  )
}

export default PhaseAccordion
